import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { guslStorage } from '../../../providers/session/GuslStorage'
import { PanelConfigDTO } from '../../types'
import { SidePanelTitle } from '../gusl-table/side-panel/SidePanelTitle'
import { FilterTypeWrapperStyled, TableSidePanelWrapperStyled } from '../gusl-table/styled/side-panel/styled'
import { ColumnNameStyled, TickStyled } from '../gusl-table/styled/columns-settings/styled'
import { toggleHeaderPanelFieldVisibility } from '../gusl-table/guslTableSlice'
import { HeaderPanelSettingsBottomSheetStyled } from './styled'

interface HeaderPanelSettingsProps {
    code: string
    panels: PanelConfigDTO[]
    onClose: () => void
    isMobile?: boolean
}

export const HeaderPanelSettings = ({ code, panels, onClose, isMobile }: HeaderPanelSettingsProps): React.ReactElement => {
    const dispatch = useAppDispatch()
    const hiddenHeaderPanelFields: string[] = useAppSelector((state) => state.guslTableSlice[code]?.hiddenHeaderPanelFields ?? [])

    const hasFilterableFields = panels.some((panel) =>
        (panel.rows || []).some((row) => {
            const col = row.columns[0]
            return (col?.fields || []).some((fld) => fld.filterable)
        })
    )

    if (!hasFilterableFields) return <></>

    const onToggle = (fieldKey: string) => {
        dispatch(toggleHeaderPanelFieldVisibility({ code, fieldKey }))
        const isCurrentlyHidden = hiddenHeaderPanelFields.includes(fieldKey)
        const newVisibility: Record<string, boolean> = {}
        panels.forEach((panel, pi) => {
            ;(panel.rows || []).forEach((row, ri) => {
                const col = row.columns[0]
                ;(col?.fields?.filter((fld) => fld.filterable) || []).forEach((fld) => {
                    const key = `${pi}_${ri}_0_${fld.name}`
                    newVisibility[key] = key === fieldKey ? isCurrentlyHidden : !hiddenHeaderPanelFields.includes(key)
                })
            })
        })
        guslStorage.saveHeaderPanelVisibility(code, newVisibility)
    }

    const renderRows = (): React.ReactElement => {
        return (
            <>
                {panels.map((panel, pi) =>
                    (panel.rows || []).map((row, ri) => {
                        const col = row.columns[0]
                        return (col?.fields?.filter((fld) => fld.filterable) || []).map((fld) => {
                            const fieldKey = `${pi}_${ri}_0_${fld.name}`
                            const isVisible = !hiddenHeaderPanelFields.includes(fieldKey)
                            return (
                                <div
                                    key={fieldKey}
                                    className={'d-flex align-items-center px-2 py-1'}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => onToggle(fieldKey)}
                                >
                                    <TickStyled active={isVisible}>
                                        <i className={isVisible ? 'fa-solid fa-check me-2' : 'fa-solid fa-xmark me-2'} />
                                    </TickStyled>
                                    <ColumnNameStyled active={isVisible}>{fld.label}</ColumnNameStyled>
                                </div>
                            )
                        })
                    })
                )}
            </>
        )
    }

    if (isMobile) {
        return (
            <HeaderPanelSettingsBottomSheetStyled>
                <SidePanelTitle title={'Panel Settings'} onCloseClick={onClose} />
                <FilterTypeWrapperStyled>{renderRows()}</FilterTypeWrapperStyled>
            </HeaderPanelSettingsBottomSheetStyled>
        )
    }

    return (
        <TableSidePanelWrapperStyled>
            <SidePanelTitle title={'Panel Settings'} onCloseClick={onClose} />
            <FilterTypeWrapperStyled>{renderRows()}</FilterTypeWrapperStyled>
        </TableSidePanelWrapperStyled>
    )
}
